import { Dispatch, SetStateAction, useCallback, useMemo } from "react";
import { Formik } from "formik";
import * as Yup from "yup";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";

import { RequestManagedClusterFormType } from "@/types";
import { useExplorerStore } from "@/store/explorer";
import { AuthMachineContext } from "@/store/auth";
import { RequestManagedClusterForm } from "../RequestManagedClusterForm/RequestManagedClusterForm";

type FilterType = Record<string, (string | number | boolean)[]>;

type RequestManagedClusterProps = {
  isOpen: boolean;
  onClose: Dispatch<SetStateAction<boolean>>;
  selectedFilters: FilterType;
  additionalFilters: Record<string, unknown> | undefined;
  title?: string;
  description?: string;
  onRequestSubmitted?: () => void;
  hideConnectivityTier?: boolean;
  onSubmitOverride?: (values: RequestManagedClusterFormType) => Promise<boolean>;
};

export const RequestManagedCluster: React.FC<RequestManagedClusterProps> = ({
  isOpen,
  onClose,
  selectedFilters,
  additionalFilters,
  title = "Managed Cluster Request",
  description = "Managed cluster is a custom cluster tailored to specific requirements of the client",
  onRequestSubmitted,
  hideConnectivityTier = false,
  onSubmitOverride
}) => {
  const { toast } = useToast();
  const requestManagedCluster = useExplorerStore((state) => state.requestManagedCluster);
  const profile = AuthMachineContext.useSelector((state) => state.context.profile);

  const initialValues = useMemo(() => {
    const hardwareQuantity = selectedFilters.hardwareQuantity || [];
    let gpuQuantity = 1;
    if (hardwareQuantity.length > 0) {
      gpuQuantity = (hardwareQuantity as number[]).sort(
        (a: number, b: number) => Number(b) - Number(a)
      )[0];
    }
    return {
      hardwareNames: selectedFilters.hardwareNames || [],
      regions: selectedFilters.regions || [],
      connectivityTiers: selectedFilters.connectivityTiers || [],
      gpuQuantity,
      contactEmail: profile?.email || "",
      confidentialCompute: !!selectedFilters.confidentialCompute?.[0]
    } as RequestManagedClusterFormType;
  }, [selectedFilters, profile?.email]);

  const handleSubmit = useCallback(
    async (values: RequestManagedClusterFormType) => {
      onRequestSubmitted?.();

      // Use override if provided, otherwise use default store method
      const submitFn = onSubmitOverride || requestManagedCluster;
      const isSuccess = await submitFn(values);

      if (isSuccess) {
        onClose(false);
        toast({ title: "Submitted", description: "We'll reach out to you soon." });
      } else {
        toast({
          variant: "error",
          description: "Failed to submit request, please try again later"
        });
      }
    },
    [requestManagedCluster, onSubmitOverride, onClose, toast, onRequestSubmitted]
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent showCloseIcon={true}>
        <DialogHeader>
          <DialogTitle className="title-h5">{title}</DialogTitle>
          <DialogDescription className="subtext-small pt-2">{description}</DialogDescription>
        </DialogHeader>
        <Formik
          initialValues={initialValues}
          validationSchema={getValidationSchema(hideConnectivityTier)}
          validateOnMount={true}
          onSubmit={handleSubmit}
        >
          {(formProps) => (
            <RequestManagedClusterForm
              {...formProps}
              additionalFilters={additionalFilters}
              onClose={onClose}
              hideConnectivityTier={hideConnectivityTier}
            />
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

const getValidationSchema = (hideConnectivityTier: boolean) => {
  const baseSchema = {
    hardwareNames: Yup.array().of(Yup.string()).min(1, "Select at least one hardware"),
    regions: Yup.array()
      .of(Yup.string())
      .min(1, "Select at least one region")
      .test("valid-regions", "Invalid region selected", (regions) => {
        if (!regions || regions.length === 0) return false;
        const validRegions = ["APAC", "EMEA", "LATAM", "Northern America"];
        return regions.every((r): r is string => Boolean(r) && validRegions.includes(r!));
      }),
    gpuQuantity: Yup.number().min(1, "Quantity must be at least 1").required(),
    contactEmail: Yup.string().email("Invalid email format").required("Contact email is required")
  };

  if (!hideConnectivityTier) {
    return Yup.object().shape({
      ...baseSchema,
      connectivityTiers: Yup.array()
        .of(Yup.number())
        .min(1, "Select at least one connectivity tier")
    });
  }

  return Yup.object().shape(baseSchema);
};
